// src/features/fourlink-config/FourLinkConfigTool.jsx

import React from 'react';
import * as XLSX from 'xlsx';
import {
  buildSnapshot,
  loadFromLocalStorage,
  saveToLocalStorage,
} from './utils/storage/localStorage';
import { useDataFetching } from './hooks/useDataFetching';
import { useConfigState } from './hooks/useConfigState';
import { useStorage } from './hooks/useStorage';
import { useComments } from './hooks/useComments';
import { useChecklists } from './hooks/useChecklists';
import { useTurnos } from './hooks/useTurnos';
import { useTurnoFilter } from './hooks/useTurnoFilter';

import { ProgressBar } from './components/shared/ProgressBar';
import { StepNavigation } from './components/shared/StepNavigation';
import { CommentModal } from './components/shared/CommentModal';

import { TipologiasStep } from './components/blocks/Block1Steps/TipologiasStep';
import { EquiposStep } from './components/blocks/Block1Steps/EquiposStep';
import { CategoriasProfesionalesStep } from './components/blocks/Block1Steps/CategoriasProfesionalesStep';
import { PlanesStep } from './components/blocks/Block1Steps/PlanesStep';
import { ActivosStep } from './components/blocks/Block1Steps/ActivosStep';

import { MatrizTipologiasEquipos } from './components/blocks/Block2Steps/MatrizTipologiasEquipos';
import { MatrizTipologiasActivos } from './components/blocks/Block2Steps/MatrizTipologiasActivos';
import { MatrizTareasAsignacion } from './components/blocks/Block2Steps/MatrizTareasAsignacion';
import { ConfiguracionFinal } from './components/blocks/Block2Steps/ConfiguracionFinal';
import { TareasRegistrosStep } from './components/blocks/Block1Steps/TareasRegistrosStep';

export const FourLinkConfigTool = ({ onExit, selectedInstance, apiOverrides }) => {
  // Fetch data from API
  const { isLoading, error, data } = useDataFetching(selectedInstance, apiOverrides);

  // Configuration state
  const configState = useConfigState(data);
  const [hasHydratedFromStorage, setHasHydratedFromStorage] = React.useState(false);

  // Comments management
  const comments = useComments();
  const { loadChecklist, getChecklistFromCache, isChecklistLoading } = useChecklists();

  // Turnos management
  const turnos = useTurnos(configState.tareasConfiguracion);

  // Filtro de turnos
  const turnoFilter = useTurnoFilter(turnos.horariosDisponibles);
  const [checklistModalState, setChecklistModalState] = React.useState({
    isOpen: false,
    tarea: null,
    checklistIds: [],
  });
  const [localChecklistItems, setLocalChecklistItems] = React.useState({});
  const [newChecklistItemText, setNewChecklistItemText] = React.useState({});

  const selectedInstanceId = selectedInstance && selectedInstance.id !== undefined && selectedInstance.id !== null
    ? String(selectedInstance.id)
    : '';

  const closeChecklistModal = () => {
    setChecklistModalState({ isOpen: false, tarea: null, checklistIds: [] });
    setNewChecklistItemText({});
  };

  const openChecklistModal = (tarea) => {
    if (!tarea) return;

    const ids = Array.isArray(tarea.checklists) ? tarea.checklists : Array.isArray(tarea.checklistIds) ? tarea.checklistIds : [];
    const normalizedIds = (Array.isArray(ids) ? ids : [])
      .map((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return String(item);
        }
        if (item && typeof item === 'object') {
          const candidate = item.id || item.ID || item.checklist_id || item.fk_checklist || item.guid || item.uuid;
          return candidate ? String(candidate) : null;
        }
        return null;
      })
      .filter(Boolean);

    setChecklistModalState({
      isOpen: true,
      tarea,
      checklistIds: normalizedIds,
    });
  };

  const handleAddLocalChecklistItem = (checklistId) => {
    const text = (newChecklistItemText[checklistId] || '').trim();
    if (!text) return;

    setLocalChecklistItems((prev) => {
      const prevItems = Array.isArray(prev[checklistId]) ? prev[checklistId] : [];
      return {
        ...prev,
        [checklistId]: [
          ...prevItems,
          {
            id: `local-${checklistId}-${Date.now()}`,
            nombre: text,
            isLocal: true,
          },
        ],
      };
    });

    setNewChecklistItemText((prev) => ({ ...prev, [checklistId]: '' }));
  };

  const checklistDataFromCache = React.useMemo(
    () => getChecklistFromCache(checklistModalState.checklistIds),
    [checklistModalState.checklistIds, getChecklistFromCache],
  );

  const normalizedChecklistEntries = React.useMemo(() => {
    const sources = [];

    if (Array.isArray(checklistDataFromCache)) {
      sources.push(...checklistDataFromCache);
    } else if (checklistDataFromCache) {
      sources.push(checklistDataFromCache);
    }

    if (sources.length === 0 && Array.isArray(checklistModalState.tarea?.checklists)) {
      sources.push(...checklistModalState.tarea.checklists);
    }

    if (sources.length === 0 && Array.isArray(checklistModalState.checklistIds)) {
      sources.push(...checklistModalState.checklistIds.map((id) => ({ id })));
    }

    return sources.map((entry, idx) => {
      const rawId =
        entry?.id ||
        entry?.ID ||
        entry?.checklist_id ||
        entry?.fk_checklist ||
        entry?.guid ||
        entry?.uuid ||
        entry?.nombre ||
        `checklist-${idx}`;
      const checklistId = String(rawId);
      const baseItems = Array.isArray(entry?.items) ? entry.items : [];
      const localItems = Array.isArray(localChecklistItems[checklistId]) ? localChecklistItems[checklistId] : [];

      return {
        id: checklistId,
        nombre: entry?.nombre || entry?.name || entry?.titulo || `Checklist ${idx + 1}`,
        items: [...baseItems, ...localItems],
      };
    });
  }, [checklistDataFromCache, checklistModalState.tarea, checklistModalState.checklistIds, localChecklistItems]);

  React.useEffect(() => {
    if (!checklistModalState.isOpen) return;
    if (!Array.isArray(checklistModalState.checklistIds) || checklistModalState.checklistIds.length === 0) return;
    loadChecklist(checklistModalState.checklistIds);
  }, [checklistModalState.isOpen, checklistModalState.checklistIds, loadChecklist]);

  // Persist to localStorage
  const { hasHydrated } = useStorage({
    currentStep: configState.currentStep,
    currentBlock: configState.currentBlock,
    selectedTipologias: configState.selectedTipologias,
    hiddenTipologias: configState.hiddenTipologias,
    hiddenEquipos: configState.hiddenEquipos,
    hiddenPlanes: configState.hiddenPlanes,
    hiddenCategoriasActivos: configState.hiddenCategoriasActivos,
    hiddenActivosPorCategoria: configState.hiddenActivosPorCategoria,
    hiddenCategoriasTareas: configState.hiddenCategoriasTareas,
    hiddenTareasPorCategoria: configState.hiddenTareasPorCategoria,
    hiddenGruposProfesionales: configState.hiddenGruposProfesionales,
    hiddenProfesionalesPorGrupo: configState.hiddenProfesionalesPorGrupo,
    customTipologias: configState.customTipologias,
    customEquipos: configState.customEquipos,
    customPlanes: configState.customPlanes,
    customCategoriasProfesionales: configState.customCategoriasProfesionales,
    customCategoriasActivos: configState.customCategoriasActivos,
    customCategoriasTareas: configState.customCategoriasTareas,
    centros: configState.centros,
    selectedEquipos: configState.selectedEquipos,
    equiposConfig: configState.equiposConfig,
    selectedCategorias: configState.selectedCategorias,
    selectedPlanes: configState.selectedPlanes,
    selectedActivos: configState.selectedActivos,
    activosConfig: configState.activosConfig,
    selectedTareas: configState.selectedTareas,
    registrosActivosConfig: configState.registrosActivosConfig,
    tareasConfiguracion: configState.tareasConfiguracion,
    tareaFotos: configState.tareaFotos,
    horariosDisponibles: turnos.horariosDisponibles,
    collapsedCategories: configState.collapsedCategories,
    collapsedCategoriasProfesionales: configState.collapsedCategoriasProfesionales,
    collapsedCategoriasActivos: configState.collapsedCategoriasActivos,
    tipologiaEquipoMatrix: configState.tipologiaEquipoMatrix,
    tipologiaActivoMatrix: configState.tipologiaActivoMatrix,
    tareasAsignacionMatrix: configState.tareasAsignacionMatrix,
    showVisualPreview: configState.showVisualPreview,
    comments: comments.comments,
    selectedInstance,
  });

  const tareaCumpleFiltroDeTurnos = (tareaId) => {
  if (!turnoFilter.isActive) {
    return true;
  }
  
  const metadata = turnos.turnosAsignadosPorTarea[tareaId];
  if (!metadata || !metadata.habilitado || !Array.isArray(metadata.ids) || metadata.ids.length === 0) {
    return false;
  }
  return metadata.ids.some((turnoId) => turnoFilter.selected.includes(turnoId));
};

  // Navigation handlers
  const handlePrevStep = () => {
    if (configState.currentBlock === 2 && configState.currentStep === 1) {
      configState.setCurrentBlock(1);
      configState.setCurrentStep(6);
    } else if (configState.currentStep > 1) {
      configState.setCurrentStep(configState.currentStep - 1);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNextStep = () => {
    const maxSteps = configState.currentBlock === 1 ? 6 : 4;
    if (configState.currentBlock === 1 && configState.currentStep === 6) {
      configState.setCurrentBlock(2);
      configState.setCurrentStep(1);
    } else if (configState.currentStep < maxSteps) {
      configState.setCurrentStep(configState.currentStep + 1);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const buildExportSnapshot = () => buildSnapshot({
    ...configState,
    horariosDisponibles: turnos.horariosDisponibles,
    comments: comments.comments,
    selectedInstance,
  });

  const persistSnapshot = React.useCallback(() => {
    const snapshot = buildSnapshot({
      ...configState,
      horariosDisponibles: turnos.horariosDisponibles,
      comments: comments.comments,
      selectedInstance,
    });
    saveToLocalStorage(snapshot);
    return snapshot;
  }, [configState, turnos.horariosDisponibles, comments.comments, selectedInstance]);

  React.useEffect(() => {
    if (hasHydratedFromStorage) return;
    const stored = loadFromLocalStorage();
    if (stored && (!stored.selectedInstance || !selectedInstance || String(stored.selectedInstance?.id || '') === String(selectedInstance?.id || ''))) {
      configState.hydrateFromSnapshot(stored);
      if (stored.comments && typeof stored.comments === 'object') {
        comments.setComments(stored.comments);
      }
    }
    setHasHydratedFromStorage(true);
  }, [configState, comments, hasHydratedFromStorage, selectedInstance]);

  React.useEffect(() => {
    if (!hasHydratedFromStorage) return;
    persistSnapshot();
  }, [
    hasHydratedFromStorage,
    configState.currentStep,
    configState.currentBlock,
    configState.selectedTipologias,
    configState.hiddenEquipos,
    configState.hiddenTipologias,
    configState.hiddenGruposProfesionales,
    configState.hiddenProfesionalesPorGrupo,
    configState.customTipologias,
    configState.customEquipos,
    configState.customPlanes,
    configState.hiddenPlanes,
    configState.hiddenCategoriasActivos,
    configState.hiddenActivosPorCategoria,
    configState.hiddenCategoriasTareas,
    configState.hiddenTareasPorCategoria,
    configState.customCategoriasProfesionales,
    configState.customCategoriasActivos,
    configState.customCategoriasTareas,
    configState.centros,
    configState.selectedEquipos,
    configState.equiposConfig,
    configState.selectedCategorias,
    configState.selectedPlanes,
    configState.selectedActivos,
    configState.activosConfig,
    configState.selectedTareas,
    configState.registrosActivosConfig,
    configState.tareasConfiguracion,
    configState.tareaFotos,
    turnos.horariosDisponibles,
    configState.newCentroName,
    configState.newCentroTipologia,
    configState.collapsedCategories,
    configState.collapsedCategoriasProfesionales,
    configState.collapsedCategoriasActivos,
    configState.tipologiaEquipoMatrix,
    configState.tipologiaActivoMatrix,
    configState.tareasAsignacionMatrix,
    configState.showVisualPreview,
    comments.comments,
    selectedInstance,
    persistSnapshot,
  ]);

  const handleExportSession = () => {
    try {
      const snapshot = buildExportSnapshot();
      const wb = XLSX.utils.book_new();

      const instanceSheet = XLSX.utils.json_to_sheet([{
        instancia_id: selectedInstance?.id ?? '',
        instancia_nombre: selectedInstance?.name ?? '',
        fecha_exportacion: new Date().toISOString(),
      }]);
      XLSX.utils.book_append_sheet(wb, instanceSheet, 'instancia');

      const snapshotSheet = XLSX.utils.aoa_to_sheet([
        ['key', 'value'],
        ['snapshot_json', JSON.stringify(snapshot)],
      ]);
      XLSX.utils.book_append_sheet(wb, snapshotSheet, 'snapshot');

      const comentariosData = Object.entries(comments.comments || {}).map(([key, value]) => ({
        clave: key,
        comentario: value,
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(comentariosData), 'comentarios');

      const tareasData = Object.entries(configState.tareasConfiguracion || {}).map(([tareaId, cfg]) => {
        const turnosSeleccionados = Array.isArray(cfg?.turnos?.seleccionados) ? cfg.turnos.seleccionados.join(',') : '';
        return {
          categoria: Object.entries(configState.tareasPorCategoriaDisponibles || {}).find(([, lista]) =>
            (lista || []).some((t) => (t.id || t.name || t.nombre) === tareaId)
          )?.[0] || '',
          tarea: tareaId,
          tipo_datos: cfg?.tipo_datos || '',
          periodicidad: cfg?.periodicidad || '',
          dias_semana: cfg?.dias_semana || '',
          turnos_asignados: turnosSeleccionados,
          requiere_foto: cfg?.requiere_foto ? 'si' : 'no',
        };
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tareasData), 'tareas');

      const activosData = [];
      Object.entries(configState.activosPorCategoriaDisponibles || {}).forEach(([cat, lista]) => {
        (lista || []).forEach((activo) => {
          const cfg = configState.activosConfig?.[activo] || {};
          activosData.push({
            categoria: cat,
            activo,
            seleccionado: configState.selectedActivos.includes(activo) ? 'si' : 'no',
            unidad: cfg.unidad || '',
            min: cfg.min || '',
            max: cfg.max || '',
            planes_asociados: Array.isArray(cfg.planes) ? cfg.planes.join(',') : '',
          });
        });
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(activosData), 'activos');

      const planesData = configState.planesDisponibles.map((p) => ({
        plan: p,
        oculto: configState.hiddenPlanes?.some((h) => h.toLowerCase() === p.toLowerCase()) ? 'si' : 'no',
        seleccionado: configState.selectedPlanes.includes(p) ? 'si' : 'no',
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(planesData), 'planes');

      const tipologiasData = configState.allTipologias.map((tip) => ({
        tipologia: tip,
        oculto: configState.hiddenTipologias?.some((h) => h.toLowerCase() === tip.toLowerCase()) ? 'si' : 'no',
        seleccionado: configState.selectedTipologias.includes(tip) ? 'si' : 'no',
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tipologiasData), 'tipologias');

      const equiposData = configState.allEquipos.map((eq) => ({
        equipo: eq,
        oculto: configState.hiddenEquipos?.some((h) => h.toLowerCase() === eq.toLowerCase()) ? 'si' : 'no',
        seleccionado: configState.selectedEquipos.includes(eq) ? 'si' : 'no',
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(equiposData), 'equipos');

      const categoriasProfData = [];
      Object.entries(configState.categoriasProfesionalesDisponibles || {}).forEach(([grupo, lista]) => {
        (lista || []).forEach((cat) => {
          categoriasProfData.push({
            grupo,
            categoria: cat,
            grupo_oculto: configState.hiddenGruposProfesionales?.some((g) => g.toLowerCase() === grupo.toLowerCase()) ? 'si' : 'no',
            categoria_oculta: (configState.hiddenProfesionalesPorGrupo?.[grupo] || []).some((p) => p.toLowerCase() === cat.toLowerCase()) ? 'si' : 'no',
            seleccionada: configState.selectedCategorias.includes(cat) ? 'si' : 'no',
          });
        });
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(categoriasProfData), 'categorias_prof');

      const tareasAsignacionData = Object.entries(configState.tareasAsignacionMatrix || {}).map(([key, value]) => {
        const [tareaId, tipologia, equipo, qrId] = key.split('-');
        return { tarea: tareaId, tipologia, equipo, qrId, asignado: value ? 'si' : 'no' };
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tareasAsignacionData), 'matriz_tareas_asignacion');

      const matrizEquipos = Object.entries(configState.tipologiaEquipoMatrix || {}).map(([key, qrs]) => {
        const [tipologia, equipo] = key.split('-');
        return { tipologia, equipo, qr_ids: Array.isArray(qrs) ? qrs.join(',') : '' };
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(matrizEquipos), 'matriz_equipos_tipologias');

      const matrizActivos = Object.entries(configState.tipologiaActivoMatrix || {}).map(([key, selected]) => {
        const [tipologia, activo] = key.split('-');
        return { tipologia, activo, seleccionado: selected ? 'si' : 'no' };
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(matrizActivos), 'matriz_activos_tipologias');

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fourlink-config-${selectedInstanceId || 'instancia'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('No se pudo exportar la sesión');
    }
  };

  const fileInputRef = React.useRef(null);

  const handleImportSession = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileImportChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const snapshotSheet = workbook.Sheets['snapshot'];
      if (!snapshotSheet) throw new Error('No se encontró la hoja "snapshot".');
      const rows = XLSX.utils.sheet_to_json(snapshotSheet, { header: 1 });
      const entry = rows.find((r) => r[0] === 'snapshot_json');
      if (!entry || !entry[1]) throw new Error('No se encontró el contenido del snapshot en el Excel.');
      const parsedSnapshot = JSON.parse(entry[1]);
      if (parsedSnapshot.comments) {
        comments.setComments(parsedSnapshot.comments);
      }
      if (parsedSnapshot.horariosDisponibles && typeof turnos.setHorariosDisponibles === 'function') {
        turnos.setHorariosDisponibles(parsedSnapshot.horariosDisponibles);
      }
      configState.hydrateFromSnapshot(parsedSnapshot);
      alert('Configuración importada correctamente.');
    } catch (err) {
      console.error(err);
      alert(`No se pudo importar la configuración: ${err.message}`);
    }
  };

  // Render step content
  const renderStepContent = () => {
    if (configState.currentBlock === 1) {
      switch (configState.currentStep) {
        case 1:
          return (
            <TipologiasStep
              tipologiasDisponibles={configState.tipologiasDisponibles}
              hiddenTipologias={configState.hiddenTipologias}
              selectedTipologias={configState.selectedTipologias}
              centros={configState.centros}
              onToggleTipologia={configState.toggleTipologia}
              onAddTipologia={configState.addCustomTipologia}
              onHideTipologias={configState.hideTipologias}
              onRestoreTipologia={configState.restoreTipologia}
              onAddCentro={configState.addCentro}
              onAddCentrosFromExcel={(data) => {
                // Implementar lógica de importación
                console.log('Centros importados:', data);
              }}
              onDeleteCentro={configState.deleteCentro}
              onCommentClick={comments.openModal}
              hasComment={comments.hasComment('section:tipologias')}
            />
          );

        case 2:
          return (
            <EquiposStep
              equiposDisponibles={configState.equiposDisponibles}
              hiddenEquipos={configState.hiddenEquipos}
              selectedEquipos={configState.selectedEquipos}
              equiposConfig={configState.equiposConfig}
              onToggleEquipo={configState.toggleEquipo}
              onAddEquipo={configState.addCustomEquipo}
              onUpdateQRName={configState.updateQRName}
              onToggleQRActive={configState.toggleQRActive}
              onHideEquipos={configState.hideEquipos}
              onRestoreEquipo={configState.restoreEquipo}
              onCommentClick={comments.openModal}
              hasComment={comments.hasComment('section:equipos')}
              getEquipoComment={(equipo) => comments.hasComment(`equipo:${equipo}`)}
            />
          );

        case 3:
          return (
            <CategoriasProfesionalesStep
              categoriasProfesionalesDisponibles={configState.categoriasProfesionalesDisponibles}
              hiddenGruposProfesionales={configState.hiddenGruposProfesionales}
              hiddenProfesionalesPorGrupo={configState.hiddenProfesionalesPorGrupo}
              selectedCategorias={configState.selectedCategorias}
              collapsedCategoriasProfesionales={configState.collapsedCategoriasProfesionales}
              onToggleCategoria={configState.toggleCategoria}
              onAddGrupo={configState.addCustomGrupoProfesional}
              onAddProfesionalAGrupo={configState.addCustomProfesionalAGrupo}
              onHideGrupos={configState.hideGruposProfesionales}
              onRestoreGrupo={configState.restoreGrupoProfesional}
              onHideProfesionales={configState.hideProfesionalesDeGrupo}
              onRestoreProfesional={configState.restoreProfesionalDeGrupo}
              onToggleCollapse={(categoria) => {
                configState.setCollapsedCategoriasProfesionales(prev => ({
                  ...prev,
                  [categoria]: !prev[categoria],
                }));
              }}
              onCommentClick={comments.openModal}
              hasComment={comments.hasComment('section:categorias-profesionales')}
              getCategoriaComment={(categoria) => comments.hasComment(`categoria-profesional:${categoria}`)}
            />
          );

        case 4:
          return (
            <PlanesStep
              planesDisponibles={configState.planesDisponibles}
              hiddenPlanes={configState.hiddenPlanes}
              selectedPlanes={configState.selectedPlanes}
              onTogglePlan={configState.togglePlan}
              onAddPlan={configState.addCustomPlan}
              onHidePlanes={configState.hidePlanes}
              onRestorePlan={configState.restorePlan}
              onCommentClick={comments.openModal}
              hasComment={comments.hasComment('section:planes')}
            />
          );

        case 5:
          return (
            <ActivosStep
              activosPorCategoriaDisponibles={configState.activosPorCategoriaDisponibles}
              hiddenCategoriasActivos={configState.hiddenCategoriasActivos}
              hiddenActivosPorCategoria={configState.hiddenActivosPorCategoria}
              selectedActivos={configState.selectedActivos}
              selectedPlanes={configState.selectedPlanes}
              activosConfig={configState.activosConfig}
              collapsedCategoriasActivos={configState.collapsedCategoriasActivos}
              activosErrors={[]} // TODO: Obtener del estado
              onToggleActivo={configState.toggleActivo}
              onUpdateActivoConfig={configState.updateActivoConfig}
              onAddCategoriaActivo={configState.addCustomCategoriaActivo}
              onAddActivoEnCategoria={configState.addCustomActivoEnCategoria}
              onHideCategorias={configState.hideCategoriasActivosFn}
              onRestoreCategoria={configState.restoreCategoriaActivo}
              onHideActivos={configState.hideActivosDeCategoria}
              onRestoreActivo={configState.restoreActivoDeCategoria}
              onToggleCollapse={(categoria) => {
                configState.setCollapsedCategoriasActivos(prev => ({
                  ...prev,
                  [categoria]: !prev[categoria],
                }));
              }}
              onCommentClick={comments.openModal}
              hasComment={comments.hasComment('section:activos')}
              getCategoriaComment={(categoria) => comments.hasComment(`categoria-activo:${categoria}`)}
              getActivoComment={(key) => comments.hasComment(key)}
            />
          );

    case 6:
      return (
        <TareasRegistrosStep
          tareasPorCategoriaDisponibles={configState.tareasPorCategoriaDisponibles}
          hiddenCategoriasTareas={configState.hiddenCategoriasTareas}
          hiddenTareasPorCategoria={configState.hiddenTareasPorCategoria}
          selectedTareas={configState.selectedTareas}
          collapsedCategories={configState.collapsedCategories}
          tareasConfiguracion={configState.tareasConfiguracion}
          selectedPlanes={configState.selectedPlanes}
          selectedActivos={configState.selectedActivos}
          activosConfig={configState.activosConfig}
          registrosActivosConfig={configState.registrosActivosConfig}
          tareaFotos={configState.tareaFotos}
          horariosDisponibles={turnos.horariosDisponibles}
          tareasErrors={[]}
          onToggleTarea={configState.toggleTarea}
          onToggleCollapse={(categoria) => {
            configState.setCollapsedCategories(prev => ({
              ...prev,
              [categoria]: !prev[categoria],
            }));
          }}
          onAddCategoriaTarea={configState.addCustomCategoriaTarea}
          onAddTareaEnCategoria={configState.addCustomTareaEnCategoria}
          onHideCategorias={configState.hideCategoriasTareasFn}
          onRestoreCategoria={configState.restoreCategoriaTarea}
          onHideTareas={configState.hideTareasDeCategoria}
          onRestoreTarea={configState.restoreTareaDeCategoria}
          onTareaConfigChange={(tareaId, field, value) => {
            configState.setTareasConfiguracion((prev) => {
              const prevConfig = prev[tareaId] || {};
              return {
                ...prev,
                [tareaId]: {
                  ...prevConfig,
                  [field]: value,
                },
              };
            });
          }}
          onUpdateRegistrosConfig={(tareaId, config) => {
            configState.setRegistrosActivosConfig((prev) => ({
              ...prev,
              [tareaId]: config,
            }));
          }}
          onFotoChange={(tareaId, file) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              configState.setTareaFotos((prev) => ({
                ...prev,
                [tareaId]: {
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  dataUrl: e.target.result,
                },
              }));
            };
            reader.readAsDataURL(file);
          }}
          onFotoRemove={(tareaId) => {
            configState.setTareaFotos((prev) => {
              const next = { ...prev };
              delete next[tareaId];
              return next;
            });
          }}
          onAddHorario={turnos.addHorario}
          onCommentClick={comments.openModal}
          onChecklistClick={(tarea) => {
            openChecklistModal(tarea);
          }}
          hasSectionComment={comments.hasComment('section:tareas-registros')}
          getCategoriaComment={(categoria) => comments.hasComment(`categoria-tarea:${categoria}`)}
          comments={comments.comments}
          // Filtro de turnos - ACTUALIZADO
          turnoFilterOptions={turnoFilter.options}
          turnoFilterFilteredOptions={turnoFilter.filteredOptions}
          turnoFilterSelected={turnoFilter.selected}
          turnoFilterOpen={turnoFilter.isOpen}
          turnoFilterSearch={turnoFilter.search}
          turnoFilterButtonText={turnoFilter.buttonText}
          isTurnoFilterActive={turnoFilter.isActive}
          onTurnoFilterToggle={turnoFilter.toggle}
          onTurnoFilterSearchChange={turnoFilter.setSearch}
          onTurnoFilterSelectionToggle={turnoFilter.toggleSelection}
          onTurnoFilterClear={turnoFilter.clear}
          turnoFilterRef={turnoFilter.dropdownRef}
          tareaCumpleFiltroDeTurnos={tareaCumpleFiltroDeTurnos}
        />
      );

      default:
        return <div>Paso no encontrado</div>;
    }
    } else {
    // Bloque 2
    switch (configState.currentStep) {
      case 1:
        return (
          <MatrizTipologiasEquipos
            selectedTipologias={configState.selectedTipologias}
            selectedEquipos={configState.selectedEquipos}
            equiposConfig={configState.equiposConfig}
            tipologiaEquipoMatrix={configState.tipologiaEquipoMatrix}
            onToggleTipologiaEquipoSelection={configState.toggleTipologiaEquipoSelection}
          />
        );

      case 2:
        return (
          <MatrizTipologiasActivos
            selectedTipologias={configState.selectedTipologias}
            selectedActivos={configState.selectedActivos}
            activosConfig={configState.activosConfig}
            tipologiaActivoMatrix={configState.tipologiaActivoMatrix}
            onToggleTipologiaActivoSelection={configState.toggleTipologiaActivoSelection}
          />
        );

      case 3:
  return (
    <MatrizTareasAsignacion
      selectedTipologias={configState.selectedTipologias}
      selectedEquipos={configState.selectedEquipos}
      selectedTareas={configState.selectedTareas}
      equiposConfig={configState.equiposConfig}
      tipologiaEquipoMatrix={configState.tipologiaEquipoMatrix}
      tareasAsignacionMatrix={configState.tareasAsignacionMatrix}
      tareasPorCategoriaDisponibles={configState.tareasPorCategoriaDisponibles}
      onToggleTareaAsignacion={configState.toggleTareaAsignacion}
      // Filtro de turnos - ACTUALIZADO
      turnoFilterOptions={turnoFilter.options}
      turnoFilterSelected={turnoFilter.selected}
      turnoFilterOpen={turnoFilter.isOpen}
      turnoFilterSearch={turnoFilter.search}
      onTurnoFilterToggle={turnoFilter.toggle}
      onTurnoFilterSearchChange={turnoFilter.setSearch}
      onTurnoFilterSelectionToggle={turnoFilter.toggleSelection}
      onTurnoFilterClear={turnoFilter.clear}
      turnoFilterRef={turnoFilter.dropdownRef}
      turnosAsignadosPorTarea={turnos.turnosAsignadosPorTarea}
    />
  );

      case 4:
        return (
          <ConfiguracionFinal
            selectedTipologias={configState.selectedTipologias}
            centros={configState.centros}
            selectedEquipos={configState.selectedEquipos}
            equiposConfig={configState.equiposConfig}
            selectedCategorias={configState.selectedCategorias}
            selectedPlanes={configState.selectedPlanes}
            selectedActivos={configState.selectedActivos}
            activosConfig={configState.activosConfig}
            selectedTareas={configState.selectedTareas}
            tareasPorCategoriaDisponibles={configState.tareasPorCategoriaDisponibles}
            tipologiaEquipoMatrix={configState.tipologiaEquipoMatrix}
            tipologiaActivoMatrix={configState.tipologiaActivoMatrix}
            tareasAsignacionMatrix={configState.tareasAsignacionMatrix}
            onGenerateExcel={() => {
              // Implementar generación de Excel
              alert('Función de generación de Excel por implementar');
            }}
          />
        );

      default:
        return <div>Paso no encontrado</div>;
    }
  }
};

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando datos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 max-w-lg">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error al cargar los datos</h2>
          <p className="text-gray-700">{error}</p>
          <button
            onClick={() => {
              persistSnapshot();
              if (onExit) onExit();
            }}
            className="mt-6 px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      {/* Modals */}
      <CommentModal
        isOpen={comments.isModalOpen}
        onClose={comments.closeModal}
        comment={comments.modalData.comment}
        onChange={comments.handleChange}
        onSave={comments.handleSave}
        targetLabel={comments.modalData.targetLabel}
      />

      {checklistModalState.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Checklist</p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {checklistModalState.tarea?.name || checklistModalState.tarea?.nombre || 'Tarea'}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeChecklistModal}
                className="text-2xl leading-none text-gray-500 hover:text-gray-700"
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto px-6 py-4 space-y-6">
              {isChecklistLoading(checklistModalState.checklistIds) && (
                <p className="text-sm text-gray-600">Cargando checklist...</p>
              )}
              {normalizedChecklistEntries.map((chk) => (
                <div key={chk.id || chk.nombre} className="rounded-lg border border-gray-200 p-4 bg-gray-50">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h4 className="text-md font-semibold text-gray-900">Elementos</h4>
                    <div className="flex flex-1 sm:flex-none items-center gap-2 justify-end">
                      <input
                        type="text"
                        value={newChecklistItemText[chk.id] || ''}
                        onChange={(e) =>
                          setNewChecklistItemText((prev) => ({ ...prev, [chk.id]: e.target.value }))
                        }
                        placeholder="Nuevo elemento del checklist"
                        className="w-full sm:w-80 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => handleAddLocalChecklistItem(chk.id)}
                        className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
                      >
                        <span className="sr-only">Anadir elemento</span>
                        <span aria-hidden="true" className="text-base font-bold">+</span>
                      </button>
                    </div>
                  </div>

                  {chk.items && chk.items.length > 0 ? (
                    <ul className="space-y-2">
                      {chk.items.map((item, idx) => (
                        <li
                          key={item.id || item.ID || item.guid || item.uuid || `${chk.id}-item-${idx}`}
                          className="rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
                        >
                          {item.titulo || item.nombre || item.name || item.descripcion || item.label || 'Elemento'}
                          {item.isLocal && (
                            <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                              Local
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-600">No hay elementos en este checklist.</p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={closeChecklistModal}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Header buttons */}
        <div className="flex justify-between items-center gap-2 mb-4">
          <div>
            {configState.currentBlock === 1 && configState.currentStep === 1 && (
              <button
                onClick={handleImportSession}
                className="px-3 py-2 rounded-md text-sm font-medium border border-green-200 text-green-700 bg-white hover:bg-green-50 transition-colors"
                title="Importar configuración desde Excel"
              >
                Importar
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportSession}
              className="px-3 py-2 rounded-md text-sm font-medium border border-blue-200 text-blue-700 bg-white hover:bg-blue-50 transition-colors"
              title="Exportar configuración a Excel"
            >
              Exportar
            </button>
            <button
              onClick={() => {
                persistSnapshot();
                if (onExit) onExit();
              }}
              className="px-3 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              title="Volver a la pantalla inicial"
            >
              Salir
            </button>
          </div>
        </div>

        {/* Title */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-8 mt-10">
          <div>
            <h1 className="text-4xl font-bold mb-3" style={{ color: '#1a365d' }}>
              Herramienta de Configuración 4Link
            </h1>
            <p className="text-gray-600 text-lg">
              Configura de forma asistida los centros del cliente
            </p>
          </div>
        </div>

        {/* Instance info */}
        {selectedInstance && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-8 flex flex-col gap-1">
            <span className="text-sm font-semibold text-blue-800 uppercase tracking-wide">
              Instancia seleccionada
            </span>
            <span className="text-2xl font-bold text-blue-900">
              {selectedInstance.name || selectedInstance.id}
            </span>
            <span className="text-sm text-blue-800">ID: {selectedInstance.id}</span>
            {selectedInstance.description && (
              <span className="text-sm text-blue-700">{selectedInstance.description}</span>
            )}
          </div>
        )}

        {/* Progress bar */}
        <ProgressBar
          currentBlock={configState.currentBlock}
          currentStep={configState.currentStep}
          maxSteps={configState.currentBlock === 1 ? 6 : 4}
        />

        {/* Step navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <StepNavigation
            currentBlock={configState.currentBlock}
            currentStep={configState.currentStep}
            maxSteps={configState.currentBlock === 1 ? 6 : 4}
            onPrev={handlePrevStep}
            onNext={handleNextStep}
            onStepChange={configState.setCurrentStep}
            onBlockChange={configState.setCurrentBlock}
          />
        </div>

        {/* Main content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-8">
          {renderStepContent()}
        </div>

        {/* Bottom navigation */}
        <div className="flex justify-between">
          <button
            onClick={handlePrevStep}
            disabled={configState.currentBlock === 1 && configState.currentStep === 1}
            className="flex items-center gap-2 px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
          >
            &larr; Anterior
          </button>

          <button
            onClick={handleNextStep}
            disabled={configState.currentBlock === 2 && configState.currentStep === 4}
            className="flex items-center gap-2 px-6 py-3 text-white rounded-lg hover:opacity-90 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
            style={{ backgroundColor: '#3182ce' }}
          >
            Siguiente &rarr;
          </button>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleFileImportChange}
      />
    </div>
  );
};

export default FourLinkConfigTool;
